import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Typography
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from "react-router-dom";
import Body from "../components/layout/Body";
import cancelButtonSx from "../styles/cancelButtonSx";
import { useAuth } from "../store/auth-context";
import useApiHook from "../hooks/useApiHook";

const DEFAULT_SETTINGS = {
    inApp: true,
    email: true,
    whatsapp: true,
    push: true
};

const SETTING_ITEMS = [
    {
        key: 'inApp',
        title: 'Campanella interna',
        description: "Mostra l'avviso nell'app quando la trascrizione e pronta."
    },
    {
        key: 'email',
        title: 'Email',
        description: "Invia un'email quando la trascrizione e completata."
    },
    {
        key: 'whatsapp',
        title: 'WhatsApp',
        description: 'Invia un messaggio WhatsApp a fine trascrizione.'
    },
    {
        key: 'push',
        title: 'Push mobile',
        description: 'Invia una notifica push sul dispositivo mobile.'
    }
];

const NotificheTrascrizione = () => {
    const navigate = useNavigate();
    const authCtx = useAuth();
    const user = authCtx.user;
    const isLoading = authCtx.isLoading;
    const { response: apiResponse, makeRequest, error: apiError } = useApiHook();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const lastResponseRef = useRef(null);
    const lastErrorRef = useRef(null);

    useEffect(() => {
        if (!isLoading && !user) {
            navigate('/auth');
        }
    }, [isLoading, navigate, user]);

    useEffect(() => {
        if (!user) {
            return;
        }

        setSettings({
            ...DEFAULT_SETTINGS,
            ...(user.transcriptionNotificationSettings || {})
        });
        setInitialSettings({
            ...DEFAULT_SETTINGS,
            ...(user.transcriptionNotificationSettings || {})
        });
    }, [user]);

    useEffect(() => {
        if (apiResponse?.state !== 'success' || apiResponse === lastResponseRef.current) {
            return;
        }

        lastResponseRef.current = apiResponse;
        const updatedSettings = {
            ...DEFAULT_SETTINGS,
            ...(apiResponse.data?.transcriptionNotificationSettings || {})
        };

        authCtx.updateUser({
            transcriptionNotificationSettings: updatedSettings
        });
        setSettings(updatedSettings);
        setInitialSettings(updatedSettings);
        setSaving(false);
        setSnackbar({
            open: true,
            message: 'Impostazioni notifiche aggiornate con successo.',
            severity: 'success'
        });
    }, [apiResponse, authCtx]);

    useEffect(() => {
        if (!apiError || apiError === lastErrorRef.current) {
            return;
        }

        lastErrorRef.current = apiError;
        setSaving(false);
        setSnackbar({
            open: true,
            message: apiError.message || 'Errore durante il salvataggio delle notifiche.',
            severity: 'error'
        });
    }, [apiError]);

    const handleToggle = (key) => (event) => {
        const checked = event.target.checked;
        setSettings((currentSettings) => ({
            ...currentSettings,
            [key]: checked
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const hasChanges = Object.keys(DEFAULT_SETTINGS).some((key) => settings[key] !== initialSettings[key]);

        if (!hasChanges) {
            setSnackbar({
                open: true,
                message: 'Nessuna modifica da salvare.',
                severity: 'info'
            });
            return;
        }

        setSaving(true);
        await makeRequest('/user/transcription-notification-settings', 'post', {
            settings
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((currentSnackbar) => ({
            ...currentSnackbar,
            open: false
        }));
    };

    if (isLoading || !user) {
        return (
            <Body mainElement={(
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <CircularProgress />
                </Box>
            )} />
        );
    }

    const hasChanges = Object.keys(DEFAULT_SETTINGS).some((key) => settings[key] !== initialSettings[key]);

    return (
        <Body
            mainElement={(
                <Box sx={{ maxWidth: 920, mx: 'auto' }}>
                    <Box mb={{ xs: 2, md: 2.5 }}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                            spacing={1.5}
                        >
                            <Box>
                                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.7rem', md: '2rem' } }}>
                                    Notifiche Trascrizione
                                </Typography>
                                <Typography color="text.secondary" mt={0.75} sx={{ fontSize: '0.95rem' }}>
                                    Scegli come essere avvisato quando una trascrizione e pronta.
                                </Typography>
                            </Box>
                            <Button
                                startIcon={<ChevronLeftIcon />}
                                onClick={() => navigate('/dashboard')}
                                size="small"
                                sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-end' } }}
                            >
                                Torna indietro
                            </Button>
                        </Stack>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit}>
                        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
                            <Stack spacing={1.25}>
                                {SETTING_ITEMS.map((item) => (
                                    <Box
                                        key={item.key}
                                        sx={{
                                            py: 1,
                                            borderBottom: item.key !== SETTING_ITEMS[SETTING_ITEMS.length - 1].key ? '1px solid #f0f0f0' : 'none'
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1.5}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        >
                                            <Box sx={{ pr: { sm: 2 } }}>
                                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.98rem' }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                                    {item.description}
                                                </Typography>
                                            </Box>
                                            <FormControlLabel
                                                control={(
                                                    <Switch
                                                        checked={Boolean(settings[item.key])}
                                                        onChange={handleToggle(item.key)}
                                                    />
                                                )}
                                                label={Boolean(settings[item.key]) ? 'Attiva' : 'Disattiva'}
                                                labelPlacement="start"
                                                sx={{
                                                    ml: 0,
                                                    mr: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '0.92rem'
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            justifyContent="flex-end"
                            mt={2}
                        >
                            <Button variant="outlined" onClick={() => navigate('/dashboard')} size="small" sx={cancelButtonSx}>
                                Annulla
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                disabled={saving || !hasChanges}
                                size="small"
                                sx={{ minWidth: { xs: '100%', sm: '190px' }, minHeight: '40px' }}
                            >
                                {saving ? 'Salvataggio...' : 'Salva impostazioni'}
                            </Button>
                        </Stack>
                    </Box>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            )}
        />
    );
};

export default NotificheTrascrizione;
